import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import Grow from '@mui/material/Grow';
import GlobalStyles from '@mui/material/GlobalStyles';
import Langs, { Localize } from '../../app/lang/langs';

/**
 * Square-crop editor for a collab's profile picture.
 *
 * The browser does the cropping and the downscaling, and ships the finished 256x256 square.
 * That is deliberate: the backend has no image library, so it validates the result
 * (square, <=256px, allowed format, size cap) rather than transforming anything. Whatever
 * this component emits is what gets stored.
 *
 * Interaction: the source image is scaled to *cover* a square viewport, then dragged to
 * choose which part of it lands inside. Zoom scales up from that cover baseline, so the
 * square can never contain empty space — which is what guarantees a full-bleed 256x256
 * output with no letterboxing, whatever the shape of the file the user picked.
 *
 * `currentPhotoUrl` is the picture already on file, shown in the viewport until a new one is
 * picked, so opening the dialog says "here is your picture, replace it" rather than facing
 * someone who has a picture with an empty box. It is a preview only and is never re-cropped
 * or re-saved: what is stored is already a 256x256 square, and putting it back through the
 * crop would resample it for nothing. Save stays disabled until a file is chosen.
 */

// Largest side of the on-screen crop viewport. Independent of the output size — this is just
// how big the control is at its roomiest; OUTPUT_SIZE is what gets saved.
//
// It is a maximum, not a fixed size: at 360px the dialog has nowhere near 288px of content
// width, and a box pinned to 288 wide inside a narrower column collapses to an ellipse while
// the crop maths still believes it is 288 square — so what the user framed is not what gets
// saved. The box is therefore `width: 100%` with `aspect-ratio: 1`, and the maths follows the
// measured side (`viewportSize`) instead of this constant.
const MAX_VIEWPORT_SIZE = 288;

// Must match MAX_SIDE in the backend's CollabPhotoController. The upload is rejected above
// this, so the two constants are a contract, not a coincidence.
const OUTPUT_SIZE = 256;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

// Rejected before decoding rather than after: a 40MP phone photo would otherwise be fully
// decoded into memory just to be refused. The output is 256px, so nothing this large is
// needed as input.
const MAX_SOURCE_BYTES = 20 * 1024 * 1024;

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp'];

// Shared by the three footer buttons so they read as one set: pill, sentence case, and the
// same easing on every state change. Each one then layers its own fill on top.
const ghostButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '999px',
    px: 2.25,
    py: 0.75,
    // Three pills — Cancel / Choose another / Save — do not fit across a 360px dialog, so on
    // a phone they stack full width, and each one clears the 44px touch minimum.
    width: { xs: '100%', sm: 'auto' },
    minHeight: { xs: 44, sm: 38 },
    transition: 'background 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

/**
 * Encode the cropped canvas.
 *
 * WebP first — for a face it is meaningfully smaller than JPEG at the same quality, which
 * matters because these bytes sit in a database row. Safari could not encode WebP from a
 * canvas until 16, and `toBlob` there hands back a PNG while happily reporting success, so
 * the result's real type is checked rather than assumed and JPEG is used when it differs.
 */
const encodeCanvas = (canvas) => new Promise((resolve) => {
    canvas.toBlob((webpBlob) => {
        if (webpBlob && webpBlob.type === 'image/webp') {
            resolve(webpBlob);
            return;
        }
        canvas.toBlob((jpegBlob) => resolve(jpegBlob), 'image/jpeg', 0.9);
    }, 'image/webp', 0.9);
});

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
});

export function ProfilePictureEditor({ open, onClose, onSave, saving = false, error = null, title = null, currentPhotoUrl = null }) {

    const [imageElement, setImageElement] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    // Top-left of the scaled image relative to the viewport's top-left. Always <= 0.
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [localError, setLocalError] = useState(null);
    const [encoding, setEncoding] = useState(false);

    const fileInputRef = useRef(null);
    const dragState = useRef(null);
    const viewportRef = useRef(null);

    // The side the crop box actually got. Everything below — the cover scale, the drag clamp
    // and the source rectangle handed to drawImage — is expressed in it, so the square that
    // is saved is exactly the square that was on screen at whatever width the dialog got.
    const [viewportSize, setViewportSize] = useState(MAX_VIEWPORT_SIZE);

    useEffect(() => {
        const node = viewportRef.current;
        if (!open || !node || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver((entries) => {
            const side = Math.round(entries[0].contentRect.width);
            if (side > 0) setViewportSize(side);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [open]);

    // Scale at which the image exactly covers the viewport on its shorter side.
    const baseScale = imageElement
        ? viewportSize / Math.min(imageElement.naturalWidth, imageElement.naturalHeight)
        : 1;
    const scale = baseScale * zoom;
    const displayWidth = imageElement ? imageElement.naturalWidth * scale : 0;
    const displayHeight = imageElement ? imageElement.naturalHeight * scale : 0;

    // Keeps the viewport fully covered: the image may not be dragged past its own edges.
    const clampOffset = useCallback((next, width, height) => ({
        x: Math.min(0, Math.max(viewportSize - width, next.x)),
        y: Math.min(0, Math.max(viewportSize - height, next.y)),
    }), [viewportSize]);

    // Zooming out shrinks the image, which can leave the old offset pointing outside it —
    // so the offset is re-clamped whenever the scale changes, not only while dragging.
    useEffect(() => {
        if (!imageElement) return;
        setOffset((current) => clampOffset(current, displayWidth, displayHeight));
    }, [imageElement, displayWidth, displayHeight, clampOffset]);

    // Object URLs are a document-lifetime leak until revoked; the dialog can be opened and
    // closed repeatedly with a different file each time.
    useEffect(() => () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }, [objectUrl]);

    const resetState = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
        setImageElement(null);
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
        setLocalError(null);
        setEncoding(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileSelected = (file) => {
        if (!file) return;
        setLocalError(null);

        if (!ACCEPTED_TYPES.includes(file.type)) {
            setLocalError(Localize('PHOTO_ERROR_TYPE'));
            return;
        }
        if (file.size > MAX_SOURCE_BYTES) {
            setLocalError(Localize('PHOTO_ERROR_SOURCE_TOO_LARGE'));
            return;
        }

        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            // A source smaller than the output would be upscaled into a blurry square.
            if (Math.min(image.naturalWidth, image.naturalHeight) < 64) {
                URL.revokeObjectURL(url);
                setLocalError(Localize('PHOTO_ERROR_TOO_SMALL'));
                return;
            }
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            setObjectUrl(url);
            setImageElement(image);
            setZoom(MIN_ZOOM);
            // Centre the image in the viewport rather than pinning it top-left: for a wide
            // photo the middle is nearly always the wanted crop.
            const initialScale = viewportSize / Math.min(image.naturalWidth, image.naturalHeight);
            setOffset({
                x: (viewportSize - image.naturalWidth * initialScale) / 2,
                y: (viewportSize - image.naturalHeight * initialScale) / 2,
            });
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            setLocalError(Localize('PHOTO_ERROR_UNREADABLE'));
        };
        image.src = url;
    };

    // Pointer events rather than mouse+touch pairs: one code path covers mouse, touch and
    // stylus, and setPointerCapture keeps the drag alive when the cursor leaves the box.
    const handlePointerDown = (event) => {
        if (!imageElement) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragState.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };
    };

    const handlePointerMove = (event) => {
        if (!dragState.current) return;
        const { startX, startY, originX, originY } = dragState.current;
        setOffset(clampOffset(
            { x: originX + (event.clientX - startX), y: originY + (event.clientY - startY) },
            displayWidth,
            displayHeight,
        ));
    };

    const handlePointerUp = (event) => {
        if (!dragState.current) return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        dragState.current = null;
    };

    const handleSave = async () => {
        if (!imageElement) return;
        setEncoding(true);
        setLocalError(null);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = OUTPUT_SIZE;
            canvas.height = OUTPUT_SIZE;
            const context = canvas.getContext('2d');

            // The viewport shows a `scale`d image at `offset`; the source rectangle is that
            // mapping inverted back into the image's own pixel coordinates.
            const sourceX = -offset.x / scale;
            const sourceY = -offset.y / scale;
            const sourceSide = viewportSize / scale;

            // Smooths the downscale; without it a large photo reduced to 256px aliases badly.
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';

            // A transparent PNG would otherwise crop to transparent pixels, which render as
            // black in some contexts once re-encoded to JPEG.
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

            context.drawImage(
                imageElement,
                sourceX, sourceY, sourceSide, sourceSide,
                0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
            );

            const blob = await encodeCanvas(canvas);
            if (!blob) {
                setLocalError(Localize('PHOTO_ERROR_ENCODE'));
                setEncoding(false);
                return;
            }

            const dataUrl = await blobToDataUrl(blob);
            setEncoding(false);
            // The parent owns the request and the success/failure feedback; this component
            // only produces the image.
            onSave(dataUrl);
        } catch (encodeError) {
            setEncoding(false);
            setLocalError(Localize('PHOTO_ERROR_ENCODE'));
        }
    };

    const busy = saving || encoding;
    const shownError = error || localError;
    // The stored picture is shown only until a file is picked — from then on the viewport
    // belongs to the crop.
    const showingCurrent = !imageElement && !!currentPhotoUrl;

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : handleClose}
            maxWidth="xs"
            fullWidth
            // Scale-in rather than the default fade: the panel is glass, and it reads as a
            // pane of it settling into place instead of a box appearing.
            TransitionComponent={Grow}
            transitionDuration={{ enter: 280, exit: 180 }}
            slotProps={{
                backdrop: {
                    sx: {
                        // The page behind is part of the material — frosted, not hidden.
                        backgroundColor: 'rgba(10, 20, 40, 0.42)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                    },
                },
                paper: {
                    sx: {
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: { xs: '22px', sm: '28px' },
                        // MUI's default 32px margin costs 64px of a 360px screen, which is
                        // what pushed the crop circle down to an oval. Not fullScreen: the
                        // whole point of the panel is the page frosted behind it, and a
                        // 288px circle stranded in a full-height sheet looks worse than a
                        // card that fills the width it needs.
                        m: { xs: 1.5, sm: 4 },
                        width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
                        maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
                        // Frosted pane: a translucent gradient over whatever is behind it, a
                        // hairline highlight along the top edge, and a shadow deep enough to
                        // lift it off the backdrop.
                        background: 'linear-gradient(155deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.66) 100%)',
                        backdropFilter: 'blur(30px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(30px) saturate(180%)',
                        border: '1px solid rgba(255,255,255,0.72)',
                        boxShadow: '0 28px 70px rgba(12, 26, 54, 0.30), inset 0 1px 0 rgba(255,255,255,0.95)',
                        // Safari before 15.4 and Firefox with the flag off ignore
                        // backdrop-filter, and a 66%-opaque panel over a busy page is
                        // unreadable. The fallback is simply a solid panel.
                        '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
                            background: 'linear-gradient(155deg, #ffffff 0%, #f4f6fb 100%)',
                        },
                    },
                },
            }}
        >
            {/* Two slow-drifting colour washes under the content — the "liquid" part. Purely
                decorative, so they are hidden from assistive tech and stilled for anyone who
                asked for less motion.

                Also stilled on a touch device. Three stacked backdrop-filters (the backdrop,
                this panel, the replace overlay) plus two 48px blurs is a compositing bill a
                mid-range phone pays every frame, and the drift is the only part of it that
                repaints. The colour stays; only the movement goes. */}
            <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                <Box sx={{
                    position: 'absolute', top: '-30%', left: '-25%', width: '75%', height: '75%',
                    borderRadius: '50%', filter: 'blur(48px)', opacity: 0.55,
                    background: 'radial-gradient(circle, rgba(255,144,21,0.75) 0%, rgba(255,193,0,0) 70%)',
                    animation: 'ppeDriftA 14s ease-in-out infinite',
                    '@media (prefers-reduced-motion: reduce), (hover: none)': { animation: 'none' },
                }} />
                <Box sx={{
                    position: 'absolute', bottom: '-32%', right: '-22%', width: '70%', height: '70%',
                    borderRadius: '50%', filter: 'blur(52px)', opacity: 0.42,
                    background: 'radial-gradient(circle, rgba(17,121,190,0.70) 0%, rgba(46,170,250,0) 70%)',
                    animation: 'ppeDriftB 17s ease-in-out infinite',
                    '@media (prefers-reduced-motion: reduce), (hover: none)': { animation: 'none' },
                }} />
                <GlobalStyles styles={{
                    '@keyframes ppeDriftA': {
                        '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
                        '50%': { transform: 'translate3d(14%, 10%, 0) scale(1.12)' },
                    },
                    '@keyframes ppeDriftB': {
                        '0%, 100%': { transform: 'translate3d(0, 0, 0) scale(1.05)' },
                        '50%': { transform: 'translate3d(-12%, -9%, 0) scale(0.95)' },
                    },
                }} />
            </Box>

            {/* The paper is a flex column, so this wrapper has to be one too — otherwise the
                title / content / actions stack is sized by its own content and simply
                overflows a short viewport (a phone in landscape, or a laptop with the
                devtools open), where the paper's `overflow: hidden` then eats the buttons. */}
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* `title` is passed when an admin is setting someone else's picture, so the
                    dialog says whose. Own-picture use passes nothing and keeps the generic
                    heading. */}
                <DialogTitle sx={{
                    textAlign: 'center',
                    fontSize: '1.08rem',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    color: 'rgba(16, 32, 64, 0.92)',
                    pb: 1,
                }}>
                    {title || <Langs str="PHOTO_EDIT_TITLE" />}
                </DialogTitle>

                {/* `overflowY: auto` rather than the `visible` this had: visible defeats the
                    dialog's own scrolling, so on any viewport shorter than the panel the
                    circle and the buttons were clipped with no way to reach them. */}
                <DialogContent sx={{ overflowY: 'auto', minHeight: 0, px: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2.25} alignItems="center">

                        {shownError ? (
                            <Alert
                                severity="error"
                                sx={{
                                    width: '100%',
                                    borderRadius: '14px',
                                    backgroundColor: 'rgba(251, 105, 98, 0.12)',
                                    border: '1px solid rgba(251, 105, 98, 0.32)',
                                    backdropFilter: 'blur(6px)',
                                }}
                            >
                                {shownError}
                            </Alert>
                        ) : null}

                        {/* Gradient ring around the crop circle. A padded wrapper rather than a
                            border, because a border cannot hold a gradient and still clip the
                            image to a circle. */}
                        <Box sx={{
                            p: '3px',
                            // Width-led, not fixed: whatever the dialog can spare, up to the
                            // 288 it wants. The +6 is the ring's own padding.
                            width: '100%',
                            maxWidth: `${MAX_VIEWPORT_SIZE + 6}px`,
                            alignSelf: 'center',
                            boxSizing: 'border-box',
                            borderRadius: '50%',
                            background: (imageElement || showingCurrent)
                                ? 'linear-gradient(140deg, #FF8701 0%, #FFC100 45%, rgba(255,255,255,0.85) 100%)'
                                : 'linear-gradient(140deg, rgba(255,255,255,0.9) 0%, rgba(180,196,220,0.55) 100%)',
                            boxShadow: (imageElement || showingCurrent)
                                ? '0 14px 34px rgba(255, 135, 1, 0.30)'
                                : '0 10px 26px rgba(16, 32, 64, 0.12)',
                            transition: 'background 0.35s ease, box-shadow 0.35s ease',
                        }}>
                            <Box
                                ref={viewportRef}
                                onPointerDown={handlePointerDown}
                                onPointerMove={handlePointerMove}
                                onPointerUp={handlePointerUp}
                                onPointerCancel={handlePointerUp}
                                sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    // Round, because that is how the avatar is displayed
                                    // everywhere — the crop should show what the result will
                                    // actually look like.
                                    borderRadius: '50%',
                                    background: 'linear-gradient(160deg, rgba(255,255,255,0.75) 0%, rgba(226,233,244,0.6) 100%)',
                                    // Sits the image inside the glass rather than on top of it.
                                    boxShadow: 'inset 0 2px 14px rgba(16, 32, 64, 0.16)',
                                    cursor: imageElement ? 'grab' : 'pointer',
                                    '&:active': { cursor: imageElement ? 'grabbing' : 'pointer' },
                                    // Stops the browser from panning the page instead of the
                                    // image — but only while there is an image to pan. With
                                    // the stored photo or the empty prompt showing, this box
                                    // is most of the dialog, and `none` there swallowed every
                                    // swipe over it without doing anything in exchange.
                                    touchAction: imageElement ? 'none' : 'auto',
                                    userSelect: 'none',
                                    '&:hover .replaceOverlay': { opacity: 1 },
                                }}
                                onClick={imageElement ? undefined : () => fileInputRef.current?.click()}
                            >
                                {imageElement ? (
                                    <img
                                        src={objectUrl}
                                        alt=""
                                        draggable={false}
                                        style={{
                                            position: 'absolute',
                                            left: offset.x,
                                            top: offset.y,
                                            width: displayWidth,
                                            height: displayHeight,
                                            pointerEvents: 'none',
                                        }}
                                    />
                                ) : showingCurrent ? (
                                    <>
                                        {/* The picture already on file. Not draggable and not
                                            zoomable: it is the stored 256x256 square, so there is
                                            nothing left to crop — it is here to be recognised,
                                            then replaced. */}
                                        <img
                                            src={currentPhotoUrl}
                                            alt=""
                                            draggable={false}
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        <Stack
                                            className="replaceOverlay"
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={0.75}
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                px: 3,
                                                textAlign: 'center',
                                                color: 'common.white',
                                                background: 'linear-gradient(180deg, rgba(10,20,40,0.30) 0%, rgba(10,20,40,0.62) 100%)',
                                                backdropFilter: 'blur(3px)',
                                                WebkitBackdropFilter: 'blur(3px)',
                                                opacity: 0,
                                                transition: 'opacity 0.25s ease',
                                                pointerEvents: 'none',
                                                // There is no hover on a phone, so on touch
                                                // the only thing telling anyone the stored
                                                // picture is tappable was nothing at all. It
                                                // becomes a permanent band across the bottom
                                                // of the circle instead of a full cover, so
                                                // the picture is still recognisable.
                                                '@media (hover: none)': {
                                                    opacity: 1,
                                                    top: 'auto',
                                                    height: '42%',
                                                    justifyContent: 'flex-end',
                                                    pb: 1.75,
                                                    px: 2,
                                                    background: 'linear-gradient(180deg, rgba(10,20,40,0) 0%, rgba(10,20,40,0.68) 65%)',
                                                    backdropFilter: 'none',
                                                    WebkitBackdropFilter: 'none',
                                                },
                                            }}
                                        >
                                            <PhotoCameraIcon fontSize="medium" />
                                            <Typography variant="body2" fontWeight={700} letterSpacing="0.3px">
                                                <Langs str="PHOTO_REPLACE" />
                                            </Typography>
                                        </Stack>
                                    </>
                                ) : (
                                    <Stack
                                        justifyContent="center"
                                        alignItems="center"
                                        spacing={1}
                                        // Pinned rather than `height: 100%`: the box's height
                                        // now comes from aspect-ratio, and a percentage
                                        // height against that is not worth relying on.
                                        sx={{ position: 'absolute', inset: 0, px: 3, textAlign: 'center' }}
                                    >
                                        <AddAPhotoIcon sx={{ fontSize: 34, color: 'rgba(16, 32, 64, 0.34)' }} />
                                        <Typography variant="body2" sx={{ color: 'rgba(16, 32, 64, 0.60)', fontWeight: 500 }}>
                                            <Langs str="PHOTO_PICK_PROMPT" />
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                        </Box>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_TYPES.join(',')}
                            style={{ display: 'none' }}
                            onChange={(event) => {
                                handleFileSelected(event.target.files?.[0]);
                                // Cleared so picking the same file twice in a row still fires
                                // onChange.
                                event.target.value = '';
                            }}
                        />

                        {imageElement ? (
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', px: 0.5 }}>
                                <ZoomOutIcon fontSize="small" sx={{ color: 'rgba(16, 32, 64, 0.45)' }} />
                                <Slider
                                    value={zoom}
                                    min={MIN_ZOOM}
                                    max={MAX_ZOOM}
                                    step={0.01}
                                    disabled={busy}
                                    onChange={(_event, value) => setZoom(value)}
                                    aria-label={Localize('PHOTO_ZOOM')}
                                    sx={{
                                        '& .MuiSlider-rail': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(16, 32, 64, 0.14)',
                                        },
                                        '& .MuiSlider-track': {
                                            border: 'none',
                                            background: 'linear-gradient(90deg, #FF8701 0%, #FFC100 100%)',
                                        },
                                        '& .MuiSlider-thumb': {
                                            width: 20,
                                            height: 20,
                                            backgroundColor: '#fff',
                                            border: '2px solid #FF9015',
                                            boxShadow: '0 4px 12px rgba(255, 135, 1, 0.42)',
                                            '&:hover, &.Mui-focusVisible': {
                                                boxShadow: '0 0 0 8px rgba(255, 144, 21, 0.16)',
                                            },
                                            '&.Mui-active': {
                                                boxShadow: '0 0 0 12px rgba(255, 144, 21, 0.20)',
                                            },
                                        },
                                    }}
                                />
                                <ZoomInIcon fontSize="small" sx={{ color: 'rgba(16, 32, 64, 0.45)' }} />
                            </Stack>
                        ) : null}

                        <Typography
                            variant="caption"
                            textAlign="center"
                            sx={{ color: 'rgba(16, 32, 64, 0.55)', lineHeight: 1.5 }}
                        >
                            <Langs str={imageElement
                                ? "PHOTO_DRAG_HINT"
                                : showingCurrent ? "PHOTO_CURRENT_HINT" : "PHOTO_FORMATS_HINT"} />
                        </Typography>

                    </Stack>
                </DialogContent>

                {/* column-reverse, so the DOM order (Cancel, Choose, Save) reads bottom-up on
                    a phone and Save lands under the thumb rather than Cancel. */}
                <DialogActions
                    disableSpacing
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pb: { xs: 2, sm: 2.5 },
                        pt: 1,
                        gap: { xs: 1, sm: 1 },
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        onClick={handleClose}
                        disabled={busy}
                        sx={{ ...ghostButtonSx, color: 'rgba(16, 32, 64, 0.62)' }}
                    >
                        <Langs str="CANCEL" />
                    </Button>
                    <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={busy}
                        sx={{
                            ...ghostButtonSx,
                            color: '#B25F00',
                            background: 'rgba(255, 255, 255, 0.55)',
                            border: '1px solid rgba(255, 144, 21, 0.38)',
                            backdropFilter: 'blur(8px)',
                            '&:hover': {
                                background: 'rgba(255, 255, 255, 0.78)',
                                borderColor: 'rgba(255, 144, 21, 0.62)',
                            },
                        }}
                    >
                        <Langs str={(imageElement || showingCurrent) ? "PHOTO_CHANGE_FILE" : "PHOTO_CHOOSE_FILE"} />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={busy || !imageElement}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            ...ghostButtonSx,
                            px: 2.75,
                            color: '#fff',
                            background: 'linear-gradient(90deg, #FF8701 0%, #FFC100 100%)',
                            boxShadow: '0 8px 22px rgba(255, 135, 1, 0.42)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #FF8701 0%, #FFB000 100%)',
                                boxShadow: '0 12px 28px rgba(255, 135, 1, 0.52)',
                                transform: 'translateY(-1px)',
                            },
                            '&.Mui-disabled': {
                                background: 'rgba(16, 32, 64, 0.10)',
                                color: 'rgba(16, 32, 64, 0.34)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Langs str="SAVE" />
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}

export default ProfilePictureEditor;
